import { Navigate, NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BarChart3, Server, Users, Music4, Terminal, Globe, Megaphone,
  Star, BookOpen, GitBranch, LifeBuoy, ShieldBan, Activity, Crown, ScrollText,
  Settings, ListChecks, UserCog, LogOut, Zap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LogoMark, Wordmark } from "../../components/Logo";

export const ROLE_RANK = { support: 1, moderator: 2, admin: 3, owner: 4 };

const NAV = [
  { label: "Overview", items: [
    { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, min: 1 },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, min: 1 },
  ]},
  { label: "Manage", items: [
    { to: "/admin/servers", label: "Servers", icon: Server, min: 1 },
    { to: "/admin/users", label: "Users", icon: Users, min: 1 },
    { to: "/admin/music", label: "Music", icon: Music4, min: 1 },
    { to: "/admin/commands", label: "Commands", icon: Terminal, min: 1 },
  ]},
  { label: "Content", items: [
    { to: "/admin/website", label: "Website", icon: Globe, min: 3 },
    { to: "/admin/announcements", label: "Announcements", icon: Megaphone, min: 3 },
    { to: "/admin/reviews", label: "Reviews", icon: Star, min: 2 },
    { to: "/admin/docs", label: "Docs", icon: BookOpen, min: 3 },
    { to: "/admin/changelog", label: "Changelog", icon: GitBranch, min: 3 },
  ]},
  { label: "Community", items: [
    { to: "/admin/tickets", label: "Support Tickets", icon: LifeBuoy, min: 1 },
    { to: "/admin/boosters", label: "Booster Roles", icon: Star, min: 2 },
    { to: "/admin/moderation", label: "Moderation", icon: ShieldBan, min: 2 },
  ]},
  { label: "System", items: [
    { to: "/admin/status", label: "Status", icon: Activity, min: 3 },
    { to: "/admin/premium", label: "Premium", icon: Crown, min: 1 },
    { to: "/admin/ai", label: "AI Features", icon: Zap, min: 3 },
    { to: "/admin/integrations", label: "Integrations", icon: Globe, min: 3 },
    { to: "/admin/logs", label: "Logs", icon: ScrollText, min: 2 },
    { to: "/admin/settings", label: "Settings", icon: Settings, min: 3 },
    { to: "/admin/audit", label: "Audit Log", icon: ListChecks, min: 3 },
  ]},
  { label: "Owner", items: [
    { to: "/admin/staff", label: "Staff & Roles", icon: UserCog, min: 3 },
    { to: "/admin/owner", label: "Owner Panel", icon: Crown, min: 4 },
  ]},
];

export function PageTitle({ title, sub, children }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight" data-testid="admin-page-title">{title}</h1>
        {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "text-blurple", testId }) {
  return (
    <div className="rounded-lg border border-white/5 bg-panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</p>
        {Icon && <Icon size={15} className={tone} />}
      </div>
      <p className="font-heading text-2xl font-bold text-white mt-2">{value}</p>
    </div>
  );
}

export const TH = ({ children }) => (
  <th className="text-left text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold px-4 py-3 whitespace-nowrap">{children}</th>
);
export const TD = ({ children, className = "" }) => (
  <td className={`px-4 py-3.5 text-sm text-white/80 align-middle ${className}`}>{children}</td>
);

export function Badge({ tone, children }) {
  const tones = {
    green: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
    amber: "text-amber-400 border-amber-400/30 bg-amber-400/10",
    red: "text-red-400 border-red-400/30 bg-red-400/10",
    blue: "text-blurple border-blurple/30 bg-blurple/10",
    cyan: "text-volt border-volt/30 bg-volt/10",
    grey: "text-white/50 border-white/15 bg-white/5",
  };
  return (
    <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-wider border rounded-full px-2.5 py-0.5 ${tones[tone] || tones.grey}`}>
      {children}
    </span>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return <div className="min-h-screen bg-void flex items-center justify-center text-white/40 font-mono text-sm" data-testid="admin-loading">Authenticating…</div>;
  }
  if (user === false) return <Navigate to="/login" replace />;

  const rank = ROLE_RANK[user.role] || 0;

  return (
    <div className="min-h-screen bg-void flex" data-testid="admin-layout">
      <aside className="w-60 shrink-0 border-r border-white/5 bg-[#0A0A10] h-screen fixed inset-y-0 left-0 overflow-y-auto hidden lg:block" data-testid="admin-sidebar">
        <Link to="/" className="px-5 py-5 border-b border-white/5 flex items-center gap-2.5 hover:bg-white/[0.03] transition-colors duration-150" data-testid="admin-logo-home" title="Back to homepage">
          <LogoMark size={26} />
          <div>
            <Wordmark className="text-sm" />
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-widest">Admin Panel</p>
          </div>
        </Link>
        <nav className="px-3 py-4 space-y-6">
          {NAV.map((group) => {
            const items = group.items.filter((i) => rank >= i.min);
            if (!items.length) return null;
            return (
              <div key={group.label}>
                <p className="px-3 text-[10px] uppercase tracking-[0.25em] text-white/25 font-semibold mb-2">{group.label}</p>
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-150 ${
                            isActive ? "bg-blurple/15 text-white border border-blurple/30" : "text-white/55 hover:text-white hover:bg-white/5 border border-transparent"
                          }`
                        }
                      >
                        <item.icon size={15} /> {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-40 h-14 border-b border-white/5 bg-[#0A0A10]/90 backdrop-blur-md flex items-center justify-between px-6">
          <p className="text-xs font-mono text-white/40">
            <span className="text-emerald-400">●</span> bot connected · role: <span className="text-volt">{user.role}</span>
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70" data-testid="admin-user-email">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-red-400 transition-colors duration-150"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </header>
        <main className="p-6 max-w-[1400px]" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
